import { z } from 'zod';

/**
 * Frontend product validation schemas
 * These mirror the backend schemas from @llamadapro/types
 * TODO: Replace with imported schemas from @llamadapro/types once frontend has access to shared library
 */

/**
 * Validates and normalizes a price string or number.
 * Handles common formatting: removes currency symbols, commas, and whitespace.
 * Accepts both integers and decimal numbers.
 *
 * @example
 * "1,500.50" → "1500.50"
 * "$ 1500" → "1500"
 * 1500 → "1500"
 */
const priceSchema = z
  .string()
  .optional()
  .transform((val) => {
    if (!val || val.trim().length === 0) return undefined;
    // Remove common price formatting: $, commas, spaces
    const cleaned = val.trim().replace(/[$,\s]/g, '');
    return cleaned;
  })
  .refine(
    (value) => value === undefined || !Number.isNaN(Number(value)),
    // TODO: check when implementing i18n
    'Ingresa un precio válido'
  )
  .refine(
    (value) => value === undefined || Number(value) > 0,
    // TODO: check when implementing i18n
    'El precio debe ser mayor a 0'
  );

/**
 * Validates and normalizes a product name.
 * Trims whitespace and requires at least 1 character.
 */
const nameSchema = z.string().trim().min(1, 'Debes ingresar un nombre');

/**
 * Validates and normalizes a currency code.
 * Converts to uppercase and restricts to valid currency codes.
 */
const currencySchema = z
  .string()
  .transform((val) => val.toUpperCase())
  .pipe(z.enum(['CLP', 'UF']));

/**
 * Validates and normalizes a source URL.
 * Automatically adds https:// protocol if missing.
 * Trims whitespace and validates URL format.
 */
const sourceUrlSchema = z
  .string()
  .optional()
  .transform((val) => {
    if (!val || val.trim().length === 0) return undefined;
    const trimmed = val.trim();
    // Auto-add https:// if no protocol specified
    if (!trimmed.match(/^https?:\/\//)) {
      return `https://${trimmed}`;
    }
    return trimmed;
  })
  .refine((value) => {
    if (value === undefined) return true;
    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  }, 'Ingresa una URL válida');

/**
 * Validates and normalizes a product description.
 * Trims whitespace and enforces maximum length.
 */
const descriptionSchema = z
  .string()
  .optional()
  .transform((val) => val?.trim() || undefined)
  .refine(
    (value) => value === undefined || value.length <= 4000,
    'La descripción no puede exceder 4.000 caracteres'
  );

/**
 * Schema for the product form modal.
 * Validates and transforms user input with smart defaults.
 * Name and currency are required; other fields are optional but validated if provided.
 */
export const productFormSchema = z.object({
  name: nameSchema,
  description: descriptionSchema,
  price: priceSchema,
  currency: currencySchema,
  sourceUrl: sourceUrlSchema,
});

/**
 * Type inferred from the product form schema
 */
export type ProductFormValues = z.infer<typeof productFormSchema>;

/**
 * Input values accepted by the product form schema before Zod transforms/defaults.
 */
export type ProductFormInputValues = z.input<typeof productFormSchema>;
